import {
  compareAsc,
  differenceInWeeks,
  format,
  isSameDay,
  isSameWeek,
  isSameYear,
  isThisWeek,
  isThisYear,
  isToday,
  isValid,
  isYesterday,
  sub,
} from 'date-fns';
import { dateToISOString } from 'utils';
import { messageFailureStatuses, messageStatuses } from 'constants/index';

export const formatConversationTime = (time) => {
  let date = new Date(time);
  let pattern = 'MMM d, RRRR';

  date = isValid(date) ? date : Date.now();

  if (isSameDay(Date.now(), date)) {
    pattern = 'p';
  } else if (differenceInWeeks(Date.now(), date) === 0) {
    pattern = 'ccc, p';
  } else if (isSameYear(Date.now(), date)) {
    pattern = 'MMM d';
  }

  return format(date, pattern);
};

export const formatMessageTime = (time) => {
  let date = new Date(time);
  let pattern = 'h:mm aaa';

  date = isValid(date) ? date : Date.now();

  if (isSameDay(Date.now(), date)) {
    pattern = 'h:mm aaa';
  } else if (isSameWeek(Date.now(), date)) {
    pattern = 'ccc h:mm aaa';
  } else {
    pattern = 'MM/dd h:mm aaa';
  }

  return format(date, pattern);
};

export const formatGroupDate = (time) => {
  let date = new Date(time);
  let pattern = 'cccc, MMM d, RRRR';

  date = isValid(date) ? date : Date.now();

  if (isSameDay(Date.now(), date)) {
    return 'Today';
  }

  if (isSameWeek(Date.now(), date)) {
    pattern = 'cccc';
  } else if (isSameYear(Date.now(), date)) {
    pattern = 'MMM d';
  }

  return format(date, pattern);
};

export const formatCallTime = (time, isDetailed = false) => {
  let date = new Date(time);
  let pattern = 'h:mm a';

  date = isValid(date) ? date : Date.now();

  if (isToday(date)) {
    pattern = 'h:mm a';
  } else if (isThisWeek(date)) {
    pattern = 'eee';
    if (isDetailed) {
      pattern = 'eeee, h:mm a';
    }
  } else if (isThisYear(date)) {
    pattern = 'MMM d';
    if (isDetailed) {
      pattern = 'MMMM d, h:mm a';
    }
  } else {
    pattern = 'MMM d, yyyy';
    if (isDetailed) {
      pattern = 'MMMM d, yyyy, h:mm a';
    }
  }
  return format(date, pattern);
};

export const formatExpiryDate = (time) => format(new Date(time), 'dd/MM/yy');

export const isInboundImage = (fileExtension) => {
  switch (fileExtension) {
    case 'mov':
    case 'mpeg':
    case 'pdf':
    case '3gpp':
    case '3gp':
    case 'mp4':
      return false;
    default:
      return true;
  }
};

export const imageExtensionToPNG = (fileExtension, originalUrl) => {
  switch (fileExtension) {
    case 'tiff':
    case 'heic':
      return `${originalUrl.slice(0, -5)}.png`;
    default:
      return originalUrl;
  }
};

/**
 * @param {number} bytes
 */
export const convertBytesToMB = (bytes) => {
  const M = 2 ** 20;
  const thousandthOfMegabyte = 0.001;
  if (typeof bytes !== 'number' || !bytes) return 0;
  // assuming for range 1 to 1050 bytes to use 0.001 MB precision
  return Math.max(parseFloat((bytes / M).toFixed(3)), thousandthOfMegabyte);
};

export const convertBytes = (bytes) => {
  const sizes = ['Bytes', 'KB', 'MB'];

  if (!bytes) return;

  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);

  if (i === 0) return `${bytes} ${sizes[i]}`;

  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

export const addAttachmentsNotificationMsg = (message) => ({
  id: `add-attachments-${Date.now()}`,
  message: message || 'Upload failed',
  type: 'error',
  direction: 'bottomCenter',
});

/**
 * @param {Array<File>} attachments
 * @returns {number}
 */
export const getAttachmentsSize = (attachments = []) =>
  attachments.reduce((totalSize, attachment) => {
    return totalSize + convertBytesToMB(attachment?.size);
  }, 0);

/**
 * @param {string} message
 * @param {Array<File>} attachments
 * @returns {number}
 */
export const getMessageSize = (message = '', attachments = []) => {
  const attachmentsSizeInMb = getAttachmentsSize(attachments);
  const messageSizeInBytes = new Blob([message]).size;
  const messageSizeInMb = convertBytesToMB(messageSizeInBytes);

  return Number((attachmentsSizeInMb + messageSizeInMb).toFixed(3));
};

export const mapMessageNotificationData = (payload, message = {}, state) => {
  // since useSubscription hook doesn't return multiline string we use text message instead
  const textMessage =
    payload.message_direction === 'Outbound' && payload.message_status === 'delivered' && state?.textMessage;
  const msg = payload.message || '';
  let msgBody;
  let mediaUrls;
  let templateAttributes;
  try {
    const parsedMessage = JSON.parse(msg);
    msgBody = textMessage || parsedMessage?.body || '';
    mediaUrls = parsedMessage?.mediaUrls || [];
    templateAttributes = parsedMessage?.templateAttributes || [];
  } catch (err) {
    msgBody = msg;
  }
  const date = payload.date_created;
  // This is needed to fix timestamp that sometimes comes from BE as string, and causes "Invalid date" error.
  const timestampInMSLength = 13;
  const dateCreated = date?.length === timestampInMSLength ? dateToISOString(date) : date;

  return {
    ...message,
    messageId: payload.messageId || message.messageId,
    messageType: payload.message_type || message.messageType,
    messageStatus: payload.message_status?.toLowerCase() || message.messageStatus?.toLowerCase(),
    dateCreated: dateCreated || message.dateCreated,
    direction: payload.message_direction || message.direction,
    body: msgBody || message.body,
    subject: payload.message_subject || message.subject,
    mediaUrls: mediaUrls || message.mediaUrls,
    templateAttributes,
    isFailed: messageFailureStatuses.includes(
      payload.message_status?.toLowerCase() || message.messageStatus?.toLowerCase(),
    ),
  };
};

export const updateMessage = (data, payload, state) => {
  const oldMessage = data.find((message) => message.messageId === payload.messageId);
  const restMessages = data.filter((message) => message.messageId !== payload.messageId);

  return [mapMessageNotificationData(payload, oldMessage, state), ...restMessages];
};

export const markMessagesRead = (data, payload = []) => {
  const readTimeStamp = new Date();
  return data.map((message) => {
    const messageStatus = // we set status manually on UI for all incoming messages when marking them as read because we dont get status from backend on subscription update. have to keep unsubscribed status unchanged
      message.messageStatus === messageStatuses.unsubscribed
        ? { messageStatus: messageStatuses.unsubscribed, readTimeStamp }
        : { messageStatus: messageStatuses.read };
    const isMarked = payload.find((m) => m.messageId === message.messageId);
    return !isMarked ? message : { ...message, ...messageStatus };
  });
};

export const formatDateTimeStamp = (date, isMostRecentMessage, showTimeStamp) => {
  const todaysDate = new Date();

  const sentLessThanAWeekAgo = sub(todaysDate, {
    years: 0,
    months: 0,
    weeks: 0,
    days: 6,
  });
  const sentLessThanAYearAgo = sub(todaysDate, {
    years: 1,
    months: 0,
    weeks: 0,
    days: 0,
  });

  if (!isMostRecentMessage) {
    if (isToday(date)) {
      return `Today, ${format(date, 'p')}`;
    }
    if (isYesterday(date)) {
      return `Yesterday, ${format(date, 'p')}`;
    }
    if (!isYesterday(date) && compareAsc(date, sentLessThanAWeekAgo) === 1) {
      return format(date, 'eeee p');
    }
    if (compareAsc(date, sentLessThanAWeekAgo) === -1 && compareAsc(date, sentLessThanAYearAgo) === 1) {
      return format(date, 'MMMM dd, p');
    }
    if (compareAsc(date, sentLessThanAYearAgo) === -1) {
      return format(date, 'MMMM dd, yyy, p');
    }
  } else {
    if (showTimeStamp && isToday(date)) {
      return `Today, ${format(date, 'p')}`;
    }
    if (isToday(date)) {
      return format(date, 'p');
    }
    if (compareAsc(date, sentLessThanAWeekAgo) === 1) {
      return format(date, 'eee p');
    }

    if (compareAsc(date, sentLessThanAWeekAgo) === -1 && compareAsc(date, sentLessThanAYearAgo) === 1) {
      return format(date, 'MMM dd, p');
    }
    if (compareAsc(date, sentLessThanAYearAgo) === -1) {
      return format(date, 'MMM dd, yyy, p');
    }
  }
};

export const getSecondsTimestamp = () => Math.round(Date.now() / 1000);

export const generateEmailDetailsLookAttributes = (message) => {
  let templateAttributes;
  try {
    templateAttributes = JSON.parse(message?.templateAttributes);
  } catch (err) {
    templateAttributes = message?.templateAttributes;
  }
  return templateAttributes;
};
